import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { updateObservabilityConsent } from '@/lib/observability'

export interface CookiePreferences {
  essential: boolean // Always true, required for app to function
  analytics: boolean // PostHog analytics and error tracking
}

interface CookieConsentState {
  // State
  hasConsented: boolean // Has user made a choice?
  preferences: CookiePreferences
  consentDate: string | null

  // Actions
  acceptAll: () => void
  rejectOptional: () => void
  savePreferences: (preferences: Partial<CookiePreferences>) => void
  resetConsent: () => void
}

const defaultPreferences: CookiePreferences = {
  essential: true, // Always required
  analytics: false, // Opt-in by default (GDPR compliant)
}

export const useCookieConsentStore = create<CookieConsentState>()(
  persist(
    (set) => ({
      hasConsented: false,
      preferences: defaultPreferences,
      consentDate: null,

      acceptAll: () => {
        set({
          hasConsented: true,
          preferences: {
            essential: true,
            analytics: true,
          },
          consentDate: new Date().toISOString(),
        })
        // Update observability after state change is persisted
        setTimeout(() => updateObservabilityConsent(), 0)
      },

      rejectOptional: () => {
        set({
          hasConsented: true,
          preferences: {
            essential: true,
            analytics: false,
          },
          consentDate: new Date().toISOString(),
        })
        // Update observability after state change is persisted
        setTimeout(() => updateObservabilityConsent(), 0)
      },

      savePreferences: (newPreferences) => {
        set((state) => ({
          hasConsented: true,
          preferences: {
            ...state.preferences,
            ...newPreferences,
            essential: true, // Always keep essential
          },
          consentDate: new Date().toISOString(),
        }))
        // Update observability after state change is persisted
        setTimeout(() => updateObservabilityConsent(), 0)
      },

      resetConsent: () => {
        set({
          hasConsented: false,
          preferences: defaultPreferences,
          consentDate: null,
        })
      },
    }),
    {
      name: 'brightseed-cookie-consent',
      version: 1,
    }
  )
)
